import {useCallback, useEffect, useMemo, useState} from 'react';
import {APIClient} from '../../../../utils/APIClient';
import {API_URL} from '../../../../settings/local_settings';

/**
 * La biblioteca de prompts de un cliente y, con ella, el INVENTARIO de lo que su IA puede usar.
 *
 * Un solo hook para las dos superficies que lo leen en la misma página: la banda «Usa tu IA» (los
 * prompts, el prompt sugerido, los 3 más usados) y los chips del HUD del acto Alimenta («Tu IA» y
 * «La cuenta»). Antes la banda armaba esto adentro suyo y el HUD no podía decir nada de la cuenta
 * sin pedir el mismo payload por segunda vez. Va con `cache: true`, así que dos consumidores en la
 * misma pantalla comparten UN request (el `APIClient` deduplica el que está en vuelo).
 *
 * Tres estados, no dos: `available === null` es "no lo sé" (todavía no llegó, o falló), que se
 * dibuja distinto de una lista vacía — una lista vacía afirma que el asistente no ve nada del
 * cliente, y eso es otra cosa.
 */

export const LIBRARY_LOADING = 'loading';
export const LIBRARY_READY = 'ready';
export const LIBRARY_FAILED = 'failed';

// Ingredientes que el inventario no muestra: redundantes (`paid_media` es el superset de los
// canales, que ya se listan uno por uno). Mismo criterio que `IAContextMeter` — si acá se
// listaran, el HUD, la banda y el medidor dirían cosas distintas sobre el mismo cliente.
export const HIDDEN_INGREDIENTS = new Set(['paid_media']);

// `margen` (la economía del cliente cargada en Dashbo) es VISIBLE sólo con el módulo Economía del
// cliente: ahí se resuelve configurándola en `/client/:id/economia`. Sin el módulo sigue escondido,
// como hasta la tanda del 25/09 — tres prompts lo declaran como potenciador y, sin módulo, se
// mostraría como faltante con una puerta a una pantalla que la agencia no tiene.
export const ING_MARGEN = 'margen';

export function hiddenIngredientsFor(economicsEnabled) {
    return economicsEnabled ? HIDDEN_INGREDIENTS : new Set([...HIDDEN_INGREDIENTS, ING_MARGEN]);
}

// Espeja `PLAN_UNLOCK_INGREDIENTS` de `agent_tools/services/prompt_library_service.py`: los que
// faltan y NO se resuelven conectando una fuente, sino configurando algo adentro de Dashbo
// (objetivos y presupuestos de las Líneas de Control, la economía del cliente) o contratando el
// módulo (cambios en plataformas). El resto —canales, GA4, tienda, Drive, memoria— se resuelve
// conectando.
const PLAN_UNLOCK = new Set(['objetivos', 'presupuestos', ING_MARGEN, 'cambios_plataformas']);

export const UNLOCK_CONNECT = 'connect';
export const UNLOCK_CONFIGURE = 'configure';
export const UNLOCK_PLAN = 'plan';
export const UNLOCK_FEED = 'feed';

/**
 * Cómo se resuelve un ingrediente que falta, para que la pantalla ofrezca la puerta correcta y no
 * un «Conectar» genérico que lleve a un lugar donde no hay nada que conectar.
 */
export function unlockKindFor(ingredient) {
    if (ingredient === 'cambios_plataformas') return UNLOCK_PLAN;
    if (ingredient === 'memoria_reuniones') return UNLOCK_FEED;
    if (PLAN_UNLOCK.has(ingredient)) return UNLOCK_CONFIGURE;
    return UNLOCK_CONNECT;
}

/**
 * A dónde lleva resolver un ingrediente. `null` cuando no es una ruta: Drive se resuelve desde el
 * panel de Fuentes (quien consume esto abre el panel), la memoria desde el acto Alimenta de esta
 * misma página, y el módulo del plan no tiene puerta de autoservicio.
 */
export function connectPathFor(ingredient, clientId) {
    const kind = unlockKindFor(ingredient);
    if (ingredient === ING_MARGEN) return `/client/${clientId}/economia`;
    if (kind === UNLOCK_CONFIGURE) return `/client/${clientId}/control-central`;
    if (kind !== UNLOCK_CONNECT || ingredient === 'docs_drive') return null;
    return `/client/${clientId}/conections`;
}

const EMPTY = {prompts: [], ingredients: null, status: LIBRARY_LOADING};

/**
 * El inventario que se muestra: lo disponible y lo que algún prompt pide y el cliente no tiene, sin
 * los ingredientes escondidos. Lo que falta se muestra apagado, no se esconde: un inventario que sólo
 * enumera lo que ya está no dice qué falta conectar. `ingredients: null` (payload sin inventario) deja
 * `available` en `null` — «no lo pude averiguar» —, que no es lo mismo que «no tiene nada».
 */
export function inventoryFor(prompts, ingredients, hidden) {
    if (!Array.isArray(ingredients)) return {available: null, missing: []};
    const availableSet = new Set(ingredients);
    const pending = new Set();
    (prompts || []).forEach((p) => {
        [...(p.required_ingredients || []), ...(p.enhancing_ingredients || [])]
            .forEach((ing) => {
                if (!availableSet.has(ing) && !hidden.has(ing)) pending.add(ing);
            });
    });
    return {available: ingredients.filter(ing => !hidden.has(ing)), missing: [...pending]};
}

/**
 * `economicsEnabled`: el módulo Economía del cliente (`useFicha21().clientEconomicsEnabled`). Llega
 * por parámetro y no leyendo el contexto acá: el contexto arrastra `react-router`, que no carga en
 * Jest. Los dos consumidores de la página tienen que pasar el mismo valor.
 *
 * El fetch depende SÓLO del cliente: el módulo decide qué se muestra del inventario, no qué se pide.
 * Con el módulo en las deps, si `access/modules` llegaba después del montaje la banda y el HUD volvían
 * a vacío y pedían de nuevo (R-ecofe-30).
 */
export function useClientPromptLibrary(clientId, {economicsEnabled = false} = {}) {
    const [state, setState] = useState(EMPTY);

    useEffect(() => {
        if (!clientId) {
            setState(EMPTY);
            return undefined;
        }
        let alive = true;
        setState(EMPTY);
        const url = `${API_URL}agent/prompt-library/?agency_client_id=${clientId}`;
        new APIClient(url).call('GET', {}, {cache: true}).then((data) => {
            if (!alive) return;
            if (!data) {
                setState({...EMPTY, status: LIBRARY_FAILED});
                return;
            }
            // Los prompts NO cuelgan del inventario: un payload sin `available_ingredients` —un
            // campo accesorio— no puede tumbar la biblioteca entera.
            setState({
                prompts: Array.isArray(data.prompts) ? data.prompts : [],
                ingredients: Array.isArray(data.available_ingredients) ? data.available_ingredients : null,
                status: LIBRARY_READY,
            });
        }).catch(() => {
            // `available` queda en `null`: exactamente el estado "no lo pude averiguar". Sin este
            // catch la promesa rechazada quedaba sin manejar ante cualquier corte de red.
            if (alive) setState({...EMPTY, status: LIBRARY_FAILED});
        });
        return () => { alive = false; };
    }, [clientId]);

    const inventory = useMemo(
        () => inventoryFor(state.prompts, state.ingredients, hiddenIngredientsFor(economicsEnabled)),
        [state.prompts, state.ingredients, economicsEnabled],
    );

    // El prompt recién creado se agrega AL FINAL de la lista: la banda lo ubica sola (es de la
    // agencia, así que va a su lista propia —o a la tarjeta del sugerido, si es el primero— en su
    // orden), y los de Dashbo conservan el orden curado del payload.
    //
    // Y se tira el caché de la biblioteca: el GET va con `cache: true` (30 s), así que si la banda
    // se vuelve a montar dentro de ese rato —ir a «Gestionar los prompts» y volver con «atrás»— el
    // prompt recién subido desaparecía. Va por subcadena porque la key del caché es la URL completa
    // (token + `?agency_client_id=`).
    const addPrompt = useCallback((prompt) => {
        APIClient.clearCache('agent/prompt-library/');
        setState(prev => ({...prev, prompts: [...prev.prompts, prompt]}));
    }, []);

    return {prompts: state.prompts, status: state.status, ...inventory, addPrompt};
}

export default useClientPromptLibrary;
